import React, { useState } from "react";
import styles from "./CurlImporter.module.css";
import { RiAddLine, RiDeleteBin6Line } from "react-icons/ri";

// ─── Placeholder detection ───────────────────────────────────────────
// A value is a "placeholder" if it is a string that contains a
// {{ ... }} template expression anywhere in it. Values that are
// placeholders must not be stored in fieldValues because they
// would poison type inference downstream (see IntentIndex.js).
const PLACEHOLDER_RE = /\{\{\s*[^}]+?\s*\}\}/;

const isPlaceholderValue = (v) => {
  if (typeof v === "string") return PLACEHOLDER_RE.test(v);
  if (Array.isArray(v)) return v.some(isPlaceholderValue);
  if (v && typeof v === "object") {
    return Object.values(v).some(isPlaceholderValue);
  }
  return false;
};

const stripPlaceholders = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(stripPlaceholders).filter((v) => v !== undefined);
  }
  if (obj && typeof obj === "object") {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      if (isPlaceholderValue(v)) continue;
      out[k] = stripPlaceholders(v);
    }
    return out;
  }
  return obj;
};

// ─── Tokenizer ───────────────────────────────────────────────────────
//
// Split a curl command string into argv-style tokens, honoring:
//   • single quotes        'foo bar'
//   • double quotes        "foo bar"
//   • ANSI-C quotes        $'foo\nbar'
//   • backslash escapes    \" \' \\ \n
//   • line continuations   \<newline>
//
// Returns a plain array of strings. A shell-accurate tokenizer is out of
// scope; this handles the cases curl itself accepts and that people
// actually paste from browsers, Insomnia, Postman, and terminal sessions.
const tokenizeCurl = (input) => {
  const tokens = [];
  let current = "";
  let i = 0;
  let hasCurrent = false;

  const push = () => {
    if (hasCurrent) {
      tokens.push(current);
      current = "";
      hasCurrent = false;
    }
  };

  while (i < input.length) {
    const ch = input[i];

    // Whitespace is a token separator unless we're inside quotes.
    if (ch === " " || ch === "\t" || ch === "\n" || ch === "\r") {
      push();
      i += 1;
      continue;
    }

    // Line continuation: "\<newline>" means join with next line.
    if (ch === "\\" && (input[i + 1] === "\n" || input[i + 1] === "\r")) {
      i += input[i + 1] === "\r" && input[i + 2] === "\n" ? 3 : 2;
      continue;
    }

    // ANSI-C quoted string: $'...'
    if (ch === "$" && input[i + 1] === "'") {
      hasCurrent = true;
      i += 2;
      while (i < input.length && input[i] !== "'") {
        if (input[i] === "\\") {
          const next = input[i + 1];
          const escapes = {
            n: "\n",
            t: "\t",
            r: "\r",
            "\\": "\\",
            "'": "'",
            '"': '"',
          };
          current += escapes[next] !== undefined ? escapes[next] : next;
          i += 2;
        } else {
          current += input[i];
          i += 1;
        }
      }
      i += 1; // skip closing '
      continue;
    }

    // Single-quoted string: no escapes inside.
    if (ch === "'") {
      hasCurrent = true;
      i += 1;
      while (i < input.length && input[i] !== "'") {
        current += input[i];
        i += 1;
      }
      i += 1; // skip closing '
      continue;
    }

    // Double-quoted string: allow \" \\ \n \$ \`
    if (ch === '"') {
      hasCurrent = true;
      i += 1;
      while (i < input.length && input[i] !== '"') {
        if (input[i] === "\\") {
          const next = input[i + 1];
          const escapes = {
            n: "\n",
            t: "\t",
            r: "\r",
            '"': '"',
            "\\": "\\",
            $: "$",
            "`": "`",
          };
          if (escapes[next] !== undefined) {
            current += escapes[next];
            i += 2;
          } else {
            // Keep the backslash and the next char as-is.
            current += input[i];
            i += 1;
          }
        } else {
          current += input[i];
          i += 1;
        }
      }
      i += 1; // skip closing "
      continue;
    }

    // Bare token char (includes things like -X, --url, URLs).
    if (ch === "\\") {
      // Backslash outside quotes: escape the next char.
      const next = input[i + 1];
      if (next !== undefined) {
        current += next;
        hasCurrent = true;
        i += 2;
        continue;
      }
      // Trailing backslash — treat literally.
      current += "\\";
      hasCurrent = true;
      i += 1;
      continue;
    }

    current += ch;
    hasCurrent = true;
    i += 1;
  }
  push();
  return tokens;
};

// ─── Flag helpers ────────────────────────────────────────────────────
//
// curl flags can be written in several ways:
//   -X POST           (space-separated, short)
//   -XPOST            (glued short)
//   --request POST    (space-separated, long)
//   --request=POST    (equals long)
//   --requestPOST     (glued long — legal, unusual)
//
// We want a helper that returns the value and the index of the last
// consumed token for each occurrence of a given flag.
const collectFlagValues = (tokens, shortFlags, longFlags) => {
  const results = [];
  for (let i = 0; i < tokens.length; i += 1) {
    const t = tokens[i];

    // Long flag with '=' — --flag=value
    let handled = false;
    for (const lf of longFlags) {
      if (t.startsWith(`${lf}=`)) {
        results.push({ value: t.slice(lf.length + 1), idx: i });
        handled = true;
        break;
      }
      // Glued long flag — --flagVALUE (rare, but handle it)
      if (t.startsWith(lf) && t.length > lf.length) {
        results.push({ value: t.slice(lf.length), idx: i });
        handled = true;
        break;
      }
    }
    if (handled) continue;

    // Short flag with '=' — -X=value
    for (const sf of shortFlags) {
      if (t.startsWith(`${sf}=`)) {
        results.push({ value: t.slice(sf.length + 1), idx: i });
        handled = true;
        break;
      }
      // Glued short flag — -XPOST
      if (
        t.startsWith(sf) &&
        t.length > sf.length &&
        // Exclude the case where the flag is a prefix of a longer flag,
        // e.g. -d matches --data. We require the next char to not be '-'.
        t[sf.length] !== "-"
      ) {
        results.push({ value: t.slice(sf.length), idx: i });
        handled = true;
        break;
      }
    }
    if (handled) continue;

    // Space-separated — flag is a standalone token.
    if (
      shortFlags.includes(t) ||
      longFlags.includes(t)
    ) {
      const next = tokens[i + 1];
      if (next !== undefined) {
        results.push({ value: next, idx: i + 1 });
        i += 1; // skip the value
      }
    }
  }
  return results;
};

// Returns true if the flag appears anywhere (short or long, any form).
const hasFlag = (tokens, shortFlags, longFlags) => {
  for (const t of tokens) {
    if (shortFlags.includes(t) || longFlags.includes(t)) return true;
    for (const lf of longFlags) {
      if (t === lf || t.startsWith(`${lf}=`)) return true;
    }
    for (const sf of shortFlags) {
      if (t === sf) return true;
      if (t.startsWith(sf) && t.length > sf.length && t[sf.length] !== "-") {
        return true;
      }
    }
  }
  return false;
};

// ─── URL parsing ────────────────────────────────────────────────────
const parseUrlIntoParts = (rawUrl) => {
  const qIndex = rawUrl.indexOf("?");
  const baseUrl = qIndex !== -1 ? rawUrl.substring(0, qIndex) : rawUrl;
  const qs = qIndex !== -1 ? rawUrl.substring(qIndex + 1) : "";
  return { baseUrl, qs };
};

const parseQueryString = (qs) => {
  const params = {};
  if (!qs) return params;
  qs.split("&").forEach((pair) => {
    if (!pair) return;
    const eqIdx = pair.indexOf("=");
    const rawKey = eqIdx === -1 ? pair : pair.substring(0, eqIdx);
    const rawVal = eqIdx === -1 ? "" : pair.substring(eqIdx + 1);
    try {
      params[decodeURIComponent(rawKey)] = decodeURIComponent(rawVal);
    } catch {
      // Malformed percent-encoding: keep the raw strings so nothing
      // is silently dropped.
      params[rawKey] = rawVal;
    }
  });
  return params;
};

const detectDynamicSegments = (pathSegments) => {
  const newSegments = [];
  const dynamicSegments = [];

  pathSegments.forEach((seg) => {
    const isNumeric = /^\d+$/.test(seg);
    const isUuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        seg,
      );
    if (isNumeric || isUuid) {
      const placeholder = `{{ un_named_${dynamicSegments.length + 1} }}`;
      dynamicSegments.push({ placeholder, originalValue: seg });
      newSegments.push(placeholder);
    } else {
      newSegments.push(seg);
    }
  });

  return { newSegments, dynamicSegments };
};

// ─── Method inference ───────────────────────────────────────────────
//
// The rules, in order of precedence:
//
//   1. Explicit -X / --request wins, always.
//   2. -I / --head                    → HEAD
//   3. -G / --get                     → GET (forces GET even with -d)
//   4. -T / --upload-file             → PUT
//   5. -d / --data* / --json / -F / --form
//                                     → POST (curl's own rule)
//   6. Otherwise                      → GET
//
// This mirrors curl's own logic, which is documented in the curl manual:
// "If you use -d/--data, curl uses POST by default."
const inferMethod = (tokens) => {
  // 1. Explicit -X / --request
  const explicit = collectFlagValues(tokens, ["-X"], ["--request"]);
  if (explicit.length > 0) {
    return { method: explicit[0].value.toUpperCase(), source: "explicit" };
  }

  // 2. HEAD
  if (hasFlag(tokens, ["-I"], ["--head"])) {
    return { method: "HEAD", source: "head-flag" };
  }

  // 3. GET forced
  if (hasFlag(tokens, ["-G"], ["--get"])) {
    return { method: "GET", source: "get-flag" };
  }

  // 4. Upload
  if (hasFlag(tokens, ["-T"], ["--upload-file"])) {
    return { method: "PUT", source: "upload-flag" };
  }

  // 5. Body implies POST
  const hasBody =
    hasFlag(
      tokens,
      ["-d", "-F"],
      ["--data", "--data-raw", "--data-binary", "--data-urlencode", "--json", "--form"],
    );
  if (hasBody) {
    return { method: "POST", source: "body-implies-post" };
  }

  // 6. Default
  return { method: "GET", source: "default" };
};

// ─── Payload extraction ─────────────────────────────────────────────
const extractPayload = (tokens) => {
  // --json takes precedence: it's the modern curl shortcut that sets
  // Content-Type: application/json and implies POST.
  const jsonFlags = collectFlagValues(tokens, [], ["--json"]);
  if (jsonFlags.length > 0) {
    return {
      raw: jsonFlags[0].value,
      source: "json",
      contentType: "application/json",
    };
  }

  // --data-binary preserves the raw bytes; --data-raw skips '@' interpretation.
  // --data-urlencode URL-encodes; --data is the default.
  const dataFlags = collectFlagValues(
    tokens,
    ["-d"],
    ["--data", "--data-raw", "--data-binary", "--data-urlencode"],
  );
  if (dataFlags.length > 0) {
    // curl concatenates multiple --data with '&'.
    const joined = dataFlags.map((d) => d.value).join("&");
    return {
      raw: joined,
      source: "data",
      contentType: null,
    };
  }

  // -F / --form is multipart; we keep the raw string and set the marker.
  const formFlags = collectFlagValues(tokens, ["-F"], ["--form"]);
  if (formFlags.length > 0) {
    return {
      raw: formFlags.map((f) => f.value).join("&"),
      source: "form",
      contentType: "multipart/form-data",
    };
  }

  return null;
};

// ─── Header extraction ──────────────────────────────────────────────
const extractHeaders = (tokens) => {
  const headers = {};
  const headerEntries = collectFlagValues(tokens, ["-H"], ["--header"]);

  for (const { value } of headerEntries) {
    const colon = value.indexOf(":");
    if (colon === -1) continue;
    const key = value.slice(0, colon).trim();
    const val = value.slice(colon + 1).trim();
    if (key) headers[key] = val;
  }
  return headers;
};

// ─── Main parser ────────────────────────────────────────────────────
const parseCurlRaw = (curlString) => {
  const result = {
    url: "",
    method: "GET",
    methodSource: "default",
    headers: {},
    queryParams: {},
    payloadTemplate: "{}",
    fieldValues: {},
    pathVars: [],
    _warnings: [],
  };

  const cmd = curlString.trim();
  if (!cmd) return result;

  const tokens = tokenizeCurl(cmd);
  if (tokens.length === 0) return result;

  // ── 1. Find the URL. Skip the leading 'curl' token if present, then
  //       scan for the first http(s):// or /-leading token. Also honor
  //       --url as an alternative way to specify the URL.
  let urlToken = null;

  const urlFlag = collectFlagValues(tokens, [], ["--url"]);
  if (urlFlag.length > 0) {
    urlToken = urlFlag[0].value;
  } else {
    for (let i = 0; i < tokens.length; i += 1) {
      const t = tokens[i];
      if (t === "curl") continue;
      if (/^https?:\/\//i.test(t)) {
        urlToken = t;
        break;
      }
      // Relative URL — only accept it if it looks like a path and isn't
      // obviously a flag value (i.e. the previous token isn't a flag
      // that takes a value).
      if (t.startsWith("/") && !t.startsWith("//")) {
        const prev = tokens[i - 1];
        const isFlagValue =
          prev &&
          (prev === "-H" ||
            prev === "--header" ||
            prev === "-d" ||
            prev === "--data" ||
            prev === "-X" ||
            prev === "--request" ||
            prev === "-o" ||
            prev === "--output");
        if (!isFlagValue) {
          urlToken = t;
          break;
        }
      }
    }
  }

  // ── 2. Query string appended via --url-query / -G with -d
  const urlQueryFlags = collectFlagValues(tokens, [], ["--url-query"]);
  const extraQueryParts = urlQueryFlags.map((f) => f.value);

  // If -G is used with -d, curl sends the -d body as query params.
  const getForce = hasFlag(tokens, ["-G"], ["--get"]);
  if (getForce) {
    const getData = collectFlagValues(
      tokens,
      ["-d"],
      ["--data", "--data-raw", "--data-binary", "--data-urlencode"],
    );
    getData.forEach((d) => extraQueryParts.push(d.value));
  }

  if (urlToken) {
    const { baseUrl, qs } = parseUrlIntoParts(urlToken);

    let protocol = "";
    let domain = "";
    let pathWithMaybeQuery = baseUrl;

    const protocolIdx = baseUrl.indexOf("://");
    if (protocolIdx !== -1) {
      protocol = baseUrl.substring(0, protocolIdx + 3);
      const afterProtocol = baseUrl.substring(protocolIdx + 3);
      const slashIdx = afterProtocol.indexOf("/");
      if (slashIdx >= 0) {
        domain = afterProtocol.substring(0, slashIdx);
        pathWithMaybeQuery = afterProtocol.substring(slashIdx);
      } else {
        domain = afterProtocol;
        pathWithMaybeQuery = "";
      }
    }

    const segments = pathWithMaybeQuery.split("/").filter((s) => s !== "");
    const { newSegments, dynamicSegments } = detectDynamicSegments(segments);
    const newPath = newSegments.length ? "/" + newSegments.join("/") : "";
    result.pathVars = dynamicSegments;

    const queryMap = parseQueryString(qs);
    extraQueryParts.forEach((part) => {
      const map = parseQueryString(part);
      Object.assign(queryMap, map);
    });
    result.queryParams = queryMap;

    // Rebuild URL with the same query string we parsed, so nothing is lost.
    const newQs =
      Object.keys(queryMap).length > 0
        ? "?" +
          Object.entries(queryMap)
            .map(
              ([k, v]) =>
                `${encodeURIComponent(k)}=${encodeURIComponent(v)}`,
            )
            .join("&")
        : "";

    result.url = protocol + domain + newPath + newQs;
  }

  // ── 3. Headers
  result.headers = extractHeaders(tokens);

  // ── 4. Method inference (before payload, so we can honor explicit -X)
  const methodInfo = inferMethod(tokens);
  result.method = methodInfo.method;
  result.methodSource = methodInfo.source;

  // ── 5. Payload
  const payload = extractPayload(tokens);
  if (payload) {
    // If the payload came from --json, make sure Content-Type is set
    // (curl adds it implicitly; we mirror that).
    if (
      payload.contentType === "application/json" &&
      !Object.keys(result.headers).some(
        (k) => k.toLowerCase() === "content-type",
      )
    ) {
      result.headers["Content-Type"] = "application/json";
    }

    // --data with an '@' prefix means "read from file"; we can't read
    // files, so warn and strip the leading '@' so the user sees what
    // happened.
    let raw = payload.raw || "";
    if (raw.startsWith("@")) {
      result._warnings.push(
        `Payload looks like it was loaded from a file (${raw}). The file contents are not available; please paste them manually.`,
      );
      raw = raw.slice(1);
    }

    // Try to parse as JSON for concrete field values.
    try {
      const parsed = JSON.parse(raw);
      const cleaned = stripPlaceholders(parsed);
      result.fieldValues = cleaned;
      result.payloadTemplate = raw;
    } catch {
      // Not JSON — could be form-encoded (a=b&c=d) or something else.
      // Try form-encoded parsing for query-style payloads.
      const looksFormEncoded =
        raw.includes("=") && !raw.trim().startsWith("{");
      if (looksFormEncoded) {
        const formMap = parseQueryString(raw);
        result.fieldValues = formMap;
        result.payloadTemplate = raw;
      } else {
        // Opaque payload — keep as-is; fieldValues stays empty.
        result.payloadTemplate = raw;
      }
    }
  }

  return result;
};

const STATUS_OPTIONS = [
  "200",
  "201",
  "202",
  "204",
  "400",
  "401",
  "403",
  "404",
  "409",
  "422",
  "429",
  "500",
  "502",
  "503",
  "504",
];

const CurlImporter = ({ onImport, intentName, isConfigured }) => {
  const [curlText, setCurlText] = useState("");
  const [sampleResponses, setSampleResponses] = useState([
    { status: "200", body: "" },
  ]);
  const [error, setError] = useState(null);
  const [parsedPreview, setParsedPreview] = useState(null);
  const [warning, setWarning] = useState(null);
  const [importReady, setImportReady] = useState(false);

  const [pathVarMappings, setPathVarMappings] = useState({});

  // ─── Helpers for variable renaming ──────────────────────────────────
  const generateFinalUrl = () => {
    if (!parsedPreview) return "";
    let url = parsedPreview.url;
    Object.entries(pathVarMappings).forEach(([placeholder, mapping]) => {
      if (mapping.isHardcoded) {
        const pv = parsedPreview.pathVars.find(
          (p) => p.placeholder === placeholder,
        );
        if (pv) url = url.replace(placeholder, pv.originalValue);
      } else if (mapping.newName && mapping.newName.trim()) {
        url = url.replace(placeholder, `{{ ${mapping.newName.trim()} }}`);
      }
    });
    return url;
  };

  const handlePathVarNameChange = (placeholder, newName) => {
    setPathVarMappings((prev) => ({
      ...prev,
      [placeholder]: { ...prev[placeholder], newName },
    }));
  };

  const handlePathVarHardcodedToggle = (placeholder, isHardcoded) => {
    setPathVarMappings((prev) => ({
      ...prev,
      [placeholder]: { ...prev[placeholder], isHardcoded },
    }));
  };

  const handleAddSample = () => {
    setSampleResponses([...sampleResponses, { status: "200", body: "" }]);
  };

  const handleRemoveSample = (index) => {
    if (sampleResponses.length <= 1) {
      alert("You need at least one sample response.");
      return;
    }
    setSampleResponses(sampleResponses.filter((_, i) => i !== index));
  };

  const handleSampleChange = (index, field, value) => {
    const newList = [...sampleResponses];
    newList[index][field] = value;
    setSampleResponses(newList);
  };

  const handleParse = () => {
    setError(null);
    setWarning(null);
    const trimmedCurl = curlText.trim();
    if (!trimmedCurl) {
      setError("Please paste a cURL command first.");
      return;
    }

    const parsed = parseCurlRaw(curlText);
    if (!parsed.url) {
      setError("Could not extract URL from cURL command.");
      return;
    }

    if (parsed._warnings && parsed._warnings.length > 0) {
      setWarning(parsed._warnings.join(" "));
    }

    if (parsed.pathVars.length > 0) {
      const initialMappings = {};
      parsed.pathVars.forEach((pv) => {
        initialMappings[pv.placeholder] = {
          newName: "",
          isHardcoded: false,
        };
      });
      setPathVarMappings(initialMappings);
    } else {
      setPathVarMappings({});
    }

    setParsedPreview(parsed);
    setImportReady(true);
  };

  const handleConfirmImport = () => {
    if (!parsedPreview) return;

    if (hasUnresolvedPathVars()) {
      setError(
        "Please resolve every URL path variable before importing: either give it a name or mark it as 'Not a variable'.",
      );
      return;
    }

    const finalUrl = generateFinalUrl();
    const finalParsed = { ...parsedPreview, url: finalUrl };

    const responseTemplateObj = {};
    sampleResponses.forEach(({ status, body }) => {
      const statusKey = status.trim();
      if (statusKey) {
        responseTemplateObj[statusKey] = "{{ response_data | dump | safe }}";
      }
    });

    onImport(intentName, finalParsed, sampleResponses, responseTemplateObj);

    setCurlText("");
    setSampleResponses([{ status: "200", body: "" }]);
    setParsedPreview(null);
    setImportReady(false);
    setPathVarMappings({});
  };

  const hasEmptySampleBody = () =>
    sampleResponses.some((sample) => !sample.body.trim());

  const hasUnresolvedPathVars = () => {
    if (!parsedPreview || parsedPreview.pathVars.length === 0) return false;
    return parsedPreview.pathVars.some((pv) => {
      const mapping = pathVarMappings[pv.placeholder];
      if (!mapping) return true;
      if (mapping.isHardcoded) return false;
      return !(mapping.newName && mapping.newName.trim());
    });
  };

  const isPathVarResolved = (placeholder) => {
    const mapping = pathVarMappings[placeholder];
    if (!mapping) return false;
    if (mapping.isHardcoded) return true;
    return !!(mapping.newName && mapping.newName.trim());
  };

  const isParseDisabled = () => isConfigured || !curlText.trim();

  const isConfirmDisabled = () =>
    isConfigured || hasEmptySampleBody() || hasUnresolvedPathVars();

  const confirmDisabledReason = () => {
    if (!importReady) return "";
    if (hasEmptySampleBody())
      return "Add a sample response body before confirming.";
    if (hasUnresolvedPathVars())
      return "Some URL path variables are unresolved. For each one, either give it a name or check 'Not a variable'.";
    return "";
  };

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        Paste cURL to auto‑fill API configuration
      </div>
      <textarea
        value={curlText}
        onChange={(e) => {
          setCurlText(e.target.value);
          if (importReady) {
            setImportReady(false);
            setParsedPreview(null);
            setPathVarMappings({});
          }
        }}
        rows={4}
        className={styles.textarea}
        placeholder={`curl -X POST "https://api.example.com/bookings" -H "Content-Type: application/json" -d '{"room_id": 2, "check_in": "2026-10-09"}'`}
        disabled={isConfigured}
      />

      {/* ── Sample Responses ── */}
      <div className={styles.sampleSection}>
        <div className={styles.sampleHeader}>
          <label className={styles.sampleLabel}>
            Sample Responses (JSON) for different status codes
          </label>
          <button
            onClick={handleAddSample}
            className={styles.addButton}
            disabled={isConfigured}
          >
            <RiAddLine size={18} /> Add status
          </button>
        </div>
        {sampleResponses.map((sample, index) => (
          <div key={index} className={styles.sampleRow}>
            <div className={styles.sampleStatusGroup}>
              <label className={styles.statusLabel}>Status</label>
              <select
                value={sample.status}
                onChange={(e) =>
                  handleSampleChange(index, "status", e.target.value)
                }
                className={styles.statusSelect}
                disabled={isConfigured}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={sample.status}
                onChange={(e) =>
                  handleSampleChange(index, "status", e.target.value)
                }
                placeholder="Custom status"
                className={styles.statusCustomInput}
                disabled={isConfigured}
              />
              {sampleResponses.length > 1 && (
                <button
                  onClick={() => handleRemoveSample(index)}
                  className={styles.removeButton}
                  disabled={isConfigured}
                >
                  <RiDeleteBin6Line size={18} />
                </button>
              )}
            </div>
            <textarea
              value={sample.body}
              onChange={(e) =>
                handleSampleChange(index, "body", e.target.value)
              }
              rows={4}
              className={`${styles.textarea} ${styles.sampleTextarea}`}
              placeholder={`{"id":2,"room_number":"102",...}`}
              disabled={isConfigured}
            />
          </div>
        ))}
        <small className={styles.helperText}>
          The status codes will be used to pick the right template when the API
          returns different HTTP statuses.
        </small>
      </div>

      {/* ── Variables section (shown after parse, if path vars exist) ── */}
      {importReady && parsedPreview && parsedPreview.pathVars.length > 0 && (
        <div className={styles.variablesSection}>
          <h4 className={styles.variablesTitle}>URL Path Variables</h4>
          <p className={styles.variablesHint}>
            These segments were detected as dynamic IDs. You can give them a
            meaningful name or mark them as hardcoded (not a variable).
          </p>
          {parsedPreview.pathVars.map((pv) => {
            const resolved = isPathVarResolved(pv.placeholder);
            return (
              <div
                key={pv.placeholder}
                className={styles.variableRow}
                style={
                  resolved
                    ? undefined
                    : {
                        borderLeft: "3px solid #f59e0b",
                        paddingLeft: 10,
                        backgroundColor: "#fffaf0",
                      }
                }
              >
                <div className={styles.variableInfo}>
                  <span>
                    Original value: <code>{pv.originalValue}</code>
                  </span>
                  <span className={styles.placeholderPreview}>
                    Placeholder: <code>{pv.placeholder}</code>
                  </span>
                </div>
                <div className={styles.variableControls}>
                  <input
                    type="text"
                    value={pathVarMappings[pv.placeholder]?.newName || ""}
                    onChange={(e) =>
                      handlePathVarNameChange(pv.placeholder, e.target.value)
                    }
                    placeholder="e.g., booking_id"
                    className={styles.variableNameInput}
                  />
                  <label className={styles.hardcodeLabel}>
                    <input
                      type="checkbox"
                      checked={
                        pathVarMappings[pv.placeholder]?.isHardcoded || false
                      }
                      onChange={(e) =>
                        handlePathVarHardcodedToggle(
                          pv.placeholder,
                          e.target.checked,
                        )
                      }
                    />
                    Not a variable (keep original value)
                  </label>
                </div>
              </div>
            );
          })}
          <div className={styles.urlPreview}>
            <strong>URL Preview:</strong>
            <code className={styles.previewUrl}>{generateFinalUrl()}</code>
          </div>
        </div>
      )}

      {/* ── Action buttons ── */}
      <div className={styles.buttonGroup}>
        {!importReady ? (
          <button
            onClick={handleParse}
            disabled={isParseDisabled()}
            className={styles.importButton}
          >
            Parse cURL
          </button>
        ) : (
          <span
            title={confirmDisabledReason()}
            style={{ display: "inline-block" }}
          >
            <button
              onClick={handleConfirmImport}
              disabled={isConfirmDisabled()}
              className={`${styles.importButton} ${styles.isImportReady}`}
            >
              Confirm Import
            </button>
          </span>
        )}

        {error && <span className={styles.errorMessage}>{error}</span>}
        {warning && <span className={styles.warningMessage}>{warning}</span>}
      </div>

      {/* Inline hint when unresolved placeholders block Confirm */}
      {importReady && hasUnresolvedPathVars() && (
        <div
          style={{
            marginTop: 8,
            padding: "8px 12px",
            backgroundColor: "#fff3e0",
            borderLeft: "3px solid #f59e0b",
            borderRadius: 4,
            fontSize: 12,
            color: "#92400e",
          }}
        >
          ⚠️ Some URL path variables are unresolved. For each one, either give
          it a name (e.g. <code>booking_id</code>) or check{" "}
          <strong>"Not a variable (keep original value)"</strong>.
        </div>
      )}

      {/* Debug preview */}
      {parsedPreview && (
        <pre className={styles.preview}>
          {JSON.stringify(parsedPreview, null, 2)}
        </pre>
      )}
    </div>
  );
};

export default CurlImporter;